import {
    type FC,
    type ReactNode,
    useContext,
    useState,
    useEffect,
    useCallback,
    createContext,
} from 'react';
import { isMobile, isMobileOrTablet } from 'dibs-media-query';
import { localStorage } from 'dibs-browser-storage';
import { isNavbarExpandable } from './helpers/isNavbarExpandable.ts';
export const SIDE_NAV_EXPANDED_KEY = 'SELLER_SIDE_NAV_EXPANDED';

type Status = 'collapsed' | 'expanded' | 'overlay';

type NavContextType = {
    status: Status;
    expandedArea: string | null;
    activeExpandedArea: string | null;
    setStatus: (status: Status) => void;
    setExpandedArea: (expandedArea: string | null) => void;
    setActiveExpandedArea: (activeExpandedArea: string | null) => void;
};

// Default to expanded if local storage is not set and we're at full width
const getIsExpanded = (): boolean => {
    if (!isNavbarExpandable()) {
        return false;
    }
    const expanded = window.localStorage.getItem(SIDE_NAV_EXPANDED_KEY) ?? 'true';
    return expanded === 'true';
};

// Working with pre-react nunjucks templates
const expandLayout = (expanded: boolean): void => {
    const wrapperElClassList = document.getElementById('wrapper')?.classList;

    if (isMobile()) {
        wrapperElClassList?.remove('nav-collapsed', 'nav-expanded');
        wrapperElClassList?.add('mobileLayout');
    } else if (expanded) {
        wrapperElClassList?.remove('nav-collapsed', 'mobileLayout');
        wrapperElClassList?.add('nav-expanded');
    } else {
        wrapperElClassList?.remove('nav-expanded', 'mobileLayout');
        wrapperElClassList?.add('nav-collapsed');
    }
};

export const NavContext = createContext<NavContextType>({
    status: 'expanded',
    expandedArea: null,
    activeExpandedArea: null,
    setStatus: () => {},
    setExpandedArea: () => {},
    setActiveExpandedArea: () => {},
});

export const NavContextProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const [expandedArea, setExpandedArea] = useState<string | null>(null);
    const [activeExpandedArea, setActiveExpandedArea] = useState<string | null>(null);
    const [status, setStatus] = useState<Status>(() => {
        if (isMobileOrTablet()) {
            return 'collapsed';
        }
        return getIsExpanded() ? 'expanded' : 'collapsed';
    });

    const setStatusWithStorage = useCallback((newStatus: Status) => {
        /**
         * If the hamburger menu is clicked, we need to save the status in local storage
         * and also update the layout so that the content is shifted
         * only desktop syncs with localStorage!
         */
        const isExpanded = newStatus === 'expanded';
        const isCollapsed = newStatus === 'collapsed';
        if (isNavbarExpandable() && (isExpanded || isCollapsed)) {
            localStorage.setItem(SIDE_NAV_EXPANDED_KEY, isExpanded);
        }
        expandLayout(isExpanded);
        setStatus(newStatus);
    }, []);

    useEffect(() => {
        expandLayout(getIsExpanded());
    }, []);

    return (
        <NavContext
            value={{
                status,
                expandedArea, // expanded area that is currently expanded by seller choice (can only be single one)
                activeExpandedArea, // expanded area that has current (active) page inside it
                setStatus: setStatusWithStorage,
                setExpandedArea,
                setActiveExpandedArea,
            }}
        >
            {children}
        </NavContext>
    );
};

export const useNavContext = (): NavContextType => {
    const context = useContext(NavContext);
    if (!context) {
        throw new Error('NavContextProvider was not found');
    }
    return context;
};
